import React, { useEffect, useRef } from 'react';
import Toast, { ToastMessage, ToastVariant } from './Toast';
import { v4 } from 'uuid';

type ToastState = {
  uuid: string;
  message: ToastMessage;
  timeShown?: number;
  onClose?: () => void;
};

type State = {
  toasts: ToastState[];
};

type Action = {
  actionType?: 'add' | 'remove';
  uuid?: string;
  message?: ToastMessage;
  timeShown?: number;
  variant?: ToastVariant;
  onClose?: () => void;
};

type Dispatch = (action: Action) => void;

const ToastDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

function toastReducer(state: State, action: Action): State {
  switch (action.actionType) {
    case 'add':
      return {
        ...state,
        toasts: [
          ...state.toasts,
          {
            message: action?.message ?? { text: '', variant: 'info' },
            timeShown: action.timeShown,
            onClose: action.onClose,
            uuid: v4()
          }
        ]
      };
    case 'remove':
      const el = state.toasts.findIndex((item) => item.uuid === action.uuid);
      let toasts = [...state.toasts];
      toasts.splice(el, 1);
      return {
        ...state,
        toasts
      };
    default:
      return {
        ...state,
        toasts: [
          ...state.toasts,
          {
            message: action?.message ?? { text: '', variant: 'info' },
            timeShown: action.timeShown,
            onClose: action.onClose,
            uuid: v4()
          }
        ]
      };
  }
}

function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(toastReducer, {
    toasts: []
  });
  const ref = useRef<{ [uuid: string]: any }>({});

  useEffect(() => {
    // Check that all toasts have timers
    state.toasts.map((toast) => {
      if (!ref.current[toast.uuid]) {
        const t = setTimeout(() => {
          dispatch({
            actionType: 'remove',
            uuid: toast.uuid
          });
          ref.current[toast.uuid] = undefined;
        }, toast.timeShown ?? 12000);
        ref.current[toast.uuid] = t;
      }
    });
  }, [state.toasts]);

  return (
    <ToastDispatchContext.Provider value={dispatch}>
      <div
        style={{
          position: 'absolute',
          paddingBottom: 50,
          bottom: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          overflow: 'hidden',
          width: 560,
          height: 'auto'
        }}
      >
        {state.toasts.map((toast, index) => (
          <Toast
            message={toast.message}
            onClose={() => dispatch({ actionType: 'remove', uuid: toast.uuid })}
            key={toast.uuid || index}
          />
        ))}
      </div>
      {children}
    </ToastDispatchContext.Provider>
  );
}

function useToast() {
  const context = React.useContext(ToastDispatchContext);
  if (context === undefined) {
    throw new Error('ToastDispatch must be used with ToastProvider');
  }
  return context;
}

export { ToastProvider, useToast };
